/* eslint-disable import/prefer-default-export */
import {
  ref, computed, type Ref, unref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@/components/utils/notifications';
import { getFilterQuery, type Filter } from '@/components/utils/table/hooks/provider';
import type { Download } from '@/entities/Entities';
import { fetchApiBase, useFetchAction } from './fetch';
import { useDownloads } from './tasks';

export function useBlob(url?: string | Ref<string | undefined>) {
  const imageFile = ref<Blob>();
  const loading = ref(false);

  const loadImage = async () => {
    const unrefUrl = unref(url);
    if (!unrefUrl) {
      imageFile.value = undefined;
      return;
    }
    try {
      loading.value = true;
      const response = await fetchApiBase(unrefUrl);
      const blob = await response.blob();
      imageFile.value = blob;
    } catch (e) {
      imageFile.value = undefined;
    } finally {
      loading.value = false;
    }
  };

  watch(() => unref(url), loadImage, { immediate: true });

  return { blob: imageFile, loading };
}

export function useObjectUrl(blob: Blob | Ref<Blob | undefined>) {
  return computed(() => {
    const blobValue = unref(blob);
    return blobValue && blobValue instanceof Blob && URL.createObjectURL(blobValue);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Produces a `data:` URL for a blob (vs `useObjectUrl`'s `blob:` URL). Unlike a `blob:` URL — whose
 * native right-click "Save image as…" filename is a bare object-URL UUID with no extension — a
 * `data:image/png` URL makes the browser suggest a proper `download.png`. Pass `mimeType` to force
 * the declared type (e.g. `image/png`); omit it to keep the blob's own type. Returns `undefined`
 * (doing no work) until a real blob is supplied — keep the source falsy to skip the encode.
 */
export function useDataUrl(
  source: Ref<Blob | undefined> | (() => Blob | undefined),
  mimeType?: string,
) {
  const dataUrl = ref<string>();
  const loading = ref(false);
  watch(
    () => (typeof source === 'function' ? source() : unref(source)),
    async (b) => {
      if (!(b instanceof Blob)) {
        dataUrl.value = undefined;
        return;
      }
      loading.value = true;
      try {
        const typed = mimeType ? new Blob([b], { type: mimeType }) : b;
        dataUrl.value = await blobToDataUrl(typed);
      } catch {
        dataUrl.value = undefined;
      } finally {
        loading.value = false;
      }
    },
    { immediate: true },
  );
  return { dataUrl, loading };
}

export function useRemoteObjectUrl(url?: string | Ref<string | undefined>) {
  if (!url) return { url: null, loading: false };
  const { blob, loading } = useBlob(url);

  return {
    loading,
    url: useObjectUrl(blob),
  };
}

export function useFileExport(
  url: string | Ref<string>,
  filter: Filter | Ref<Filter>,
  columns: { key: string; title?: string; translations?: { [key: string]: string } }[],
  title?: string | Ref<string | undefined>,
  sort?: object,
  xlsx?: 'csv' | 'xlsx',
) {
  const notify = useNotify();
  const { t } = useI18n();

  const [exec, { loading }] = useFetchAction();
  const downloads = useDownloads();

  const startExport = async () => {
    try {
      const titleValue = unref(title);
      const download = await exec<Download>(unref(url), {
        query: getFilterQuery({ filter: unref(filter), sort: unref(sort) }).concat([
          ['columns', JSON.stringify(unref(columns))],
          ...(titleValue ? [['fileTitle', titleValue]] : []),
          ...(xlsx === 'xlsx' ? [['fileFormat', 'xlsx']] : []),
        ]),
      });
      downloads?.addTask(download);
      notify?.({
        variant: 'success',
        title: t('toolbar.export.successRequest'),
      });
    } catch (e) {
      notify?.({
        variant: 'error',
        title: t('toolbar.export.errorRequest'),
      });
    }
  };

  return [startExport, loading] as [typeof startExport, typeof loading];
}
